import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Tag, Skeleton, message, Statistic, List, Typography, Empty, Collapse, Image } from 'antd';
import TodayLessonsPanel from './TodayLessonsPanel';
import MissingProjectsPanel from './MissingProjectsPanel';
import AgendaPendingPanel from './AgendaPendingPanel';
import WorkTaskPendingPanel from './WorkTaskPendingPanel';
import RoutinesTodayPanel from './RoutinesTodayPanel';
import ParentRequestsPendingPanel from './ParentRequestsPendingPanel';
import BirthdaysPanel from './BirthdaysPanel';
import CollapsibleCard from './CollapsibleCard';
import DraggableDashboard from './DraggableDashboard';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import {
  CalendarOutlined, TeamOutlined, FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import api from '../../api/axios';
import { getDailyNotes } from '../../api/dailyNotes';
import { getDailySchedule, getMissingProjectSessions } from '../../api/schedule';
import { getAnnouncements } from '../../api/announcements';
import useAuthStore from '../../store/authStore';

dayjs.extend(isoWeek);

const { Text } = Typography;
const TYPE_LABELS = { normal: 'Normal', trial: 'Deneme', makeup: 'Telafi' };
const PIE_FILL    = ['#1677ff', '#faad14', '#ff7a45'];
const DAYS_TR     = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const TeacherDashboard = () => {
  const { user } = useAuthStore();
  const today = dayjs().format('YYYY-MM-DD');

  const [loadingMain,    setLoadingMain]    = useState(true);
  const [loadingAnn,     setLoadingAnn]     = useState(true);
  const [loadingNotes,   setLoadingNotes]   = useState(true);
  const [loadingWeek,    setLoadingWeek]    = useState(true);
  const [loadingMissing, setLoadingMissing] = useState(true);

  const [todaySessions,  setToday]          = useState([]);
  const [todaySlotList,  setTodaySlotList]  = useState([]);
  const [weekSchedule,   setWeekSchedule]   = useState({});
  const [lessonNotes,    setLessonNotes]    = useState([]);
  const [notes,          setNotes]          = useState([]);
  const [announcements,  setAnnouncements]  = useState([]);
  const [missingToday,   setMissingToday]   = useState([]);
  const [missingPast,    setMissingPast]    = useState([]);

  // Today's sessions (TodayLessonsPanel + KPI)
  useEffect(() => {
    api.get('/lesson-sessions/today')
      .then(r => setToday(r.data || []))
      .catch(() => message.error('Dersler yüklenemedi.'))
      .finally(() => setLoadingMain(false));
  }, []);

  // Today's slot data (KPI counts, type distribution)
  useEffect(() => {
    getDailySchedule({ date: today, branch_id: user?.branch_id })
      .then(r => setTodaySlotList(r.data || []))
      .catch(() => {});
  }, [today, user?.branch_id]);

  // Announcements
  useEffect(() => {
    getAnnouncements({ per_page: 3, is_active: 1 })
      .then(r => setAnnouncements(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoadingAnn(false));
  }, []);

  // Daily notes + lesson slot notes
  useEffect(() => {
    Promise.all([
      getDailyNotes({ date: today }),
      getDailySchedule({ date: today, branch_id: user?.branch_id }),
    ])
      .then(([notesRes, schedRes]) => {
        setNotes(notesRes.data?.data || notesRes.data || []);
        setLessonNotes((schedRes.data || []).filter(s => s.note));
      })
      .catch(() => {})
      .finally(() => setLoadingNotes(false));
  }, [today, user?.branch_id]);

  // Week schedule
  useEffect(() => {
    const weekStart = dayjs().startOf('isoWeek');
    const weekDates = Array.from({ length: 7 }, (_, i) =>
      weekStart.add(i, 'day').format('YYYY-MM-DD')
    );
    Promise.all(
      weekDates.map(d =>
        getDailySchedule({ date: d, branch_id: user?.branch_id })
          .then(r => ({ date: d, slots: r.data || [] }))
          .catch(() => ({ date: d, slots: [] }))
      )
    )
      .then(results => {
        const map = {};
        results.forEach(({ date, slots }) => { map[date] = slots; });
        setWeekSchedule(map);
        setTodaySlotList(map[today] || []);
      })
      .finally(() => setLoadingWeek(false));
  }, [today, user?.branch_id]);

  // Projesi girilmemiş derslerim
  useEffect(() => {
    getMissingProjectSessions()
      .then(r => {
        const rows = r.data || [];
        const grouped = {};
        rows.forEach(row => {
          const key = `${row.date}_${row.classroom}_${row.start_time}`;
          if (!grouped[key]) {
            grouped[key] = { date: row.date, start_time: row.start_time, classroom: row.classroom, students: [] };
          }
          if (row.student_name) grouped[key].students.push(row.student_name);
        });
        const sessions = Object.values(grouped);
        setMissingToday(sessions.filter(s => s.date === today));
        setMissingPast(sessions.filter(s => s.date !== today));
      })
      .catch(() => {})
      .finally(() => setLoadingMissing(false));
  }, [today]);

  const totalStudents = todaySlotList.reduce((sum, s) => sum + (s.total || 0), 0);
  const byType        = todaySlotList.reduce((acc, s) => {
    Object.entries(s.type_counts || {}).forEach(([t, c]) => { acc[t] = (acc[t] || 0) + c; });
    return acc;
  }, {});
  const typePie = Object.entries(byType).map(([type, count]) => ({ name: TYPE_LABELS[type] || type, value: count }));

  const weekDates = Array.from({ length: 7 }, (_, i) =>
    dayjs().startOf('isoWeek').add(i, 'day').format('YYYY-MM-DD')
  );

  const widgets = [];

  widgets.push({ id: 'agenda', content: <AgendaPendingPanel /> });
  widgets.push({ id: 'work-tasks', content: <WorkTaskPendingPanel /> });
  widgets.push({ id: 'routines-today', content: <RoutinesTodayPanel /> });
  widgets.push({ id: 'parent-requests', content: <ParentRequestsPendingPanel /> });
  widgets.push({ id: 'birthdays', content: <BirthdaysPanel /> });

  widgets.push({
    id: 'missing-projects',
    content: (
      <MissingProjectsPanel
        todaySessions={missingToday}
        loading={loadingMissing}
      />
    ),
  });

  if (loadingAnn || announcements.length > 0) {
    widgets.push({
      id: 'announcements',
      content: loadingAnn ? (
        <Card size="small"><Skeleton active /></Card>
      ) : (
        <CollapsibleCard title="Son Duyurular" defaultCollapsed={false}>
          <Collapse ghost size="small" items={announcements.map(a => ({
            key: a.id,
            label: (
              <div className="flex justify-between items-center w-full pr-4">
                <span className="font-semibold text-sm">{a.title}</span>
                <Text type="secondary" className="text-xs shrink-0 ml-2">{dayjs(a.published_at).format('DD.MM.YYYY')}</Text>
              </div>
            ),
            children: (
              <div style={{ padding: '4px 8px' }}>
                {a.image_url && <Image src={a.image_url} alt="" style={{ maxHeight: 180, borderRadius: 8, marginBottom: 8, display: 'block', cursor: 'pointer' }} />}
                <div className="text-gray-600 text-sm ql-editor prose max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body) }} />
              </div>
            ),
          }))} />
        </CollapsibleCard>
      ),
    });
  }

  widgets.push({
    id: 'notes',
    content: loadingNotes ? (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}><Card size="small"><Skeleton active /></Card></Col>
        <Col xs={24} lg={12}><Card size="small"><Skeleton active /></Card></Col>
      </Row>
    ) : (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><FileTextOutlined className="mr-2 text-orange-400" />Günlük Notlar</span>}
            defaultCollapsed={false}
          >
            {notes.length === 0 ? (
              <Empty description="Bugün not yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={notes} renderItem={n => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {n.branch && <Tag color="geekblue" style={{ margin: 0 }}>{n.branch.name}</Tag>}
                      <Text type="secondary" className="text-xs">
                        {n.user?.name} {n.user?.surname} · {dayjs(n.created_at).format('HH:mm')}
                      </Text>
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{n.content}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><FileTextOutlined className="mr-2 text-blue-500" />Bugünün Ders Notları</span>}
            defaultCollapsed={false}
          >
            {lessonNotes.length === 0 ? (
              <Empty description="Bugün ders notu eklenmemiş" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={lessonNotes} renderItem={s => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Tag style={{ margin: 0, background: s.color ? `${s.color}22` : '#e6f4ff', color: s.color || '#1677ff', borderColor: s.color || '#91caff' }}>
                        {s.classroom_name}
                      </Tag>
                      <Text type="secondary" className="text-xs">{s.start_time?.slice(0, 5)}</Text>
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{s.note}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'kpi',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={12}>
          <Card size="small">
            <Statistic title="Bugünkü Derslerim" value={todaySlotList.length} prefix={<CalendarOutlined />} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={12}>
          <Card size="small">
            <Statistic title="Toplam Öğrenci" value={totalStudents} prefix={<TeamOutlined />} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'today-sessions',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={`Bugünkü Derslerim (${todaySlotList.length})`} size="small">
            {todaySlotList.length === 0 ? (
              <Empty description="Bugün dersin yok." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              todaySlotList.map((s, i) => (
                <div key={i} className="flex justify-between items-center p-2 border rounded mb-2">
                  <div>
                    <span className="font-semibold">{s.classroom_name}</span>
                    <span className="ml-2 text-gray-400 text-xs">{s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{s.total} öğrenci</span>
                  </div>
                </div>
              ))
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Ders Tipi Dağılımı" size="small">
            {typePie.length === 0 ? (
              <Empty description="Ders yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={typePie} cx="50%" cy="45%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
                    {typePie.map((_, i) => <Cell key={i} fill={PIE_FILL[i % PIE_FILL.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'week',
    content: loadingWeek ? (
      <Card size="small"><Skeleton active /></Card>
    ) : (
      <CollapsibleCard
        title={<span><CalendarOutlined className="mr-2" />Bu Haftaki Program</span>}
        defaultCollapsed={false}
      >
        <div className="grid grid-cols-7 gap-1">
          {weekDates.map((date, i) => {
            const slots   = weekSchedule[date] || [];
            const isToday = date === today;
            const isPast  = dayjs(date).isBefore(dayjs(), 'day');
            return (
              <div
                key={date}
                className="rounded-lg border p-1.5 min-h-20"
                style={{
                  borderColor: isToday ? '#1677ff' : '#e5e7eb',
                  background:  isToday ? '#f0f7ff' : isPast ? '#fafafa' : '#fff',
                }}
              >
                <div className="text-xs font-bold mb-1 text-center" style={{ color: isToday ? '#1677ff' : isPast ? '#9ca3af' : '#374151' }}>
                  {DAYS_TR[i]} {dayjs(date).format('DD')}
                </div>
                {slots.length === 0 ? (
                  <div className="text-center" style={{ fontSize: 9, color: '#d1d5db' }}>—</div>
                ) : (
                  slots.map((s, j) => (
                    <div
                      key={j}
                      className="rounded mb-0.5 px-1 truncate"
                      style={{
                        fontSize: 10,
                        background: s.color ? `${s.color}22` : '#e6f4ff',
                        color:      s.color || '#1677ff',
                        borderLeft: `2px solid ${s.color || '#1677ff'}`,
                      }}
                    >
                      {s.start_time?.slice(0, 5)} {s.classroom_name}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleCard>
    ),
  });

  return <DraggableDashboard storageKey="teacher" widgets={widgets} />;
};

export default TeacherDashboard;
